import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OrderApiService } from '@digilize/shared/data/services';
import { PaulyOrderLine, Shop, Show } from '@plys/src/infrastructure/dto/order.dto';
import { PaulyOrderService } from '@plys/src/infrastructure/order.service';
import { Buyer, OrderStatus } from '@shared/definitions';
import {
  BehaviorSubject,
  catchError,
  first,
  map,
  Observable,
  of,
  repeat,
  skip,
  Subject,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

export interface Voucher {
  name: string;
  url: string;
  seat: number;
  row: number;
  voucherId: string;
}

export interface OrderModel {
  id: string;
  number: string;
  vouchers: Voucher[];
  status: string;
  show: Show;
  buyer: Buyer;
  shop: Shop;
}

@Injectable({
  providedIn: 'root',
})
export class OrderState {
  private readonly MAX_RETRIES = 100;
  private readonly processed = new Subject<OrderModel>();
  private readonly order = new BehaviorSubject<OrderModel>(null);

  get order$() {
    return this.order.asObservable();
  }

  get vouchers$(): Observable<Voucher[]> {
    return this.order$.pipe(
      map((order) => {
        if (!order) {
          return [];
        }
        return order.vouchers;
      })
    );
  }

  get orderProcessed$() {
    return this.processed.asObservable();
  }

  get orderSessionStatus(): OrderStatus | null {
    const value = sessionStorage.getItem('orderStatus');
    if (value === null || value === 'null' || value === 'undefined') {
      return null;
    }
    return value as OrderStatus;
  }

  constructor(
    private readonly orderApiService: OrderApiService,
    private readonly paulyOrderApiService: PaulyOrderService
  ) {}

  pollOrder(orderId: string) {
    return this.paulyOrderApiService.getOrder(orderId).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err?.status >= 400) {
          this.processed.next(null);
        }
        return of(null);
      }),
      map((orderDto) => {
        if (!orderDto) {
          return null;
        }

        const lines = orderDto?.summary?.lines ?? [];
        return {
          id: orderDto.id,
          number: orderDto.number.toString(),
          status: orderDto.status,
          vouchers: this.mapToVouchers(lines),
          show: orderDto.summary.lines[0]?.show,
          buyer: orderDto.buyer,
          shop: orderDto.shop,
        };
      }),
      tap((order) => {
        this.setOrderSessionStatus(order?.status);
        this.order.next(order);
      }),
      tap((order) => {
        if (order && order.status !== OrderStatus.Processing) {
          this.processed.next(order);
        }
      }),
      repeat({ count: this.MAX_RETRIES, delay: (count) => timer(count * 1000) }),
      takeUntil(this.processed.pipe(skip(1)))
    );
  }

  setOrderSessionStatus(status: OrderStatus) {
    sessionStorage.setItem('orderStatus', status);
  }

  didOrderFail(orderStatus: OrderStatus): boolean {
    return [OrderStatus.Canceled, OrderStatus.Expired, OrderStatus.Failed].includes(orderStatus);
  }

  downloadVouchers(orderId: string, voucherName = 'voucher.pdf'): Observable<void> {
    return this.orderApiService.downloadVouchers(orderId).pipe(
      tap((blob) => {
        const newBlob = new Blob([blob], { type: 'application/pdf' });

        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
          (window.navigator as any).msSaveOrOpenBlob(newBlob);
          return;
        }
        const url = window.URL.createObjectURL(newBlob);

        const a = document.createElement('a');
        a.href = url;
        a.download = voucherName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }),
      map(() => void 0),
      first()
    );
  }

  private mapToVouchers(lines: PaulyOrderLine[]): Voucher[] {
    return lines
      .filter((line) => line.product_type === 6)
      .filter((line) => line.urls && line.urls.length > 0)
      .map((line) =>
        line.urls.map((url) => ({
          name: line.product_name,
          url,
          seat: line.seat?.column,
          row: line.seat?.row,
          voucherId: line.voucher_id,
        }))
      )
      .reduce((acc, val) => acc.concat(val), []);
  }
}
